import React from 'react';
import { Tooltip } from '@mui/material';
import { useAuth } from '../../context/AuthContext';

/**
 * PermissionGate — inline UI permission guard.
 *
 * Uses the same central hasPermission from AuthContext (which delegates to
 * permissions.js) so the logic is identical to ProtectedRoute and the sidebar.
 *
 * Props:
 *   permission      — single permission string to check
 *   children        — content to render when allowed
 *   fallback        — content to render when denied (default: null)
 *   showDisabled    — render children as disabled + tooltip instead of hiding
 *   disabledTooltip — tooltip text when showDisabled is true
 */
const PermissionGate = ({
  permission,
  children,
  fallback = null,
  showDisabled = false,
  disabledTooltip = 'You do not have permission to perform this action',
}) => {
  // hasPermission comes from AuthContext, which calls the central
  // hasPermission(user, permission) in permissions.js.
  // This handles super_admin, nested objects, flat arrays, dot-notation,
  // and legacy role strings — all in one place.
  const { hasPermission } = useAuth();

  const allowed = hasPermission(permission);

  if (!allowed) {
    if (showDisabled && children) {
      return (
        <Tooltip title={disabledTooltip} arrow>
          <span>
            {React.cloneElement(children, { disabled: true })}
          </span>
        </Tooltip>
      );
    }
    return fallback;
  }

  return children;
};

export default PermissionGate;
